define('presentation/analytics/facades/analyticsFacade',[
        'externalDependencies/clientWebSocket'
    ],
    function (
        /** @type typeof import('externalDependencies/clientWebSocket') */
        ClientWebSocket
    ) {
        /** @typedef {import('presentation/analytics/facades/analyticsFacade')} AnalyticsFacade */
        /** @typedef {import('common/promises/promiseFactory')} PromiseFactory   */

        /** @type {PromiseFactory} */
        let _promiseFactory = null;

        const _analyticsSocket = ClientWebSocket.forApplication("analytics");

        const _init = function (/** @type {PromiseFactory} */promiseFactory) {
            _promiseFactory = promiseFactory;
        };

        /**
         * @param {IIdType[]} filterOnHostedNumbers
           @param {IIdType[]} filterOnSystemObjects
           @param {IIdType[]} filterOnLiveReceptionistScripts
         * @returns {{ hostedNumbers: IHostedNumber[], userGroups: IUserAndUserGroup[], receptionistScripts: IReceptionistScript[] }}
         */
        const _unwrap = (filterOnHostedNumbers, filterOnSystemObjects, filterOnLiveReceptionistScripts) => {
            const hostedNumbers = filterOnHostedNumbers.map(function(item) {
                const unwrapped = ko.unwrap(item);
                return {id: unwrapped.id};
            });

            const userGroups = filterOnSystemObjects.map(function(item) {
                const unwrapped = ko.unwrap(item);
                return {id: unwrapped.id, type: unwrapped.type};
            });

            const receptionistScripts = filterOnLiveReceptionistScripts.map(function(item) {
                const unwrapped = ko.unwrap(item);
                return {id: unwrapped.id};
            });

            return {
                hostedNumbers,
                userGroups,
                receptionistScripts,
            };
        };

        /** @type {AnalyticsFacade['getCallsCsv']} */
        const _getCallsCsv = (startMoment, endMoment, callType, filterOnHostedNumbers, filterOnSystemObjects, filterOnLiveReceptionistScripts) => {
            const { hostedNumbers, userGroups, receptionistScripts } = _unwrap(filterOnHostedNumbers, filterOnSystemObjects, filterOnLiveReceptionistScripts);

            return _promiseFactory.deferIndefinitely(function(promise) {
                /** @type {IAnalyticsGetRequest} */
                const requestData = {
                    callType: callType,
                    endDate: endMoment.format(),
                    hostedNumbers: hostedNumbers,
                    receptionistScripts,
                    startDate: startMoment.format(),
                    usersAndUserGroups: userGroups,
                };

                _analyticsSocket.send(`getCallsCsv`, requestData, (results, err) => {
                    if (err) {
                        promise.reject(new Error(err));
                        return;
                    }

                    promise.resolve(results);
                });
            });
        };

         /** @type {AnalyticsFacade['getStatsCsv']} */
        const _getStatsCsv = (
            startMoment,
            endMoment,
            callType,
            filterOnHostedNumbers,
            filterOnSystemObjects,
            filterOnLiveReceptionistScripts,
            filter,
            searchByFilter) => {
            const { hostedNumbers, userGroups, receptionistScripts } = _unwrap(filterOnHostedNumbers, filterOnSystemObjects, filterOnLiveReceptionistScripts);

            return _promiseFactory.deferIndefinitely(function(promise) {
                /** @type {IGetStatsCsvRequest} */
                const requestData = {
                    callType: callType,
                    endDate: endMoment.format(),
                    hostedNumbers: hostedNumbers,
                    startDate: startMoment.format(),
                    receptionistScripts,
                    usersAndUserGroups: userGroups,
                    filter: filter,
                    searchByFilter: searchByFilter
                };

                _analyticsSocket.send(`getStatsCsv`, requestData, (results, err) => {
                    if (err) {
                        promise.reject(new Error(err));
                        return;
                    }

                    promise.resolve(results);
                });
            });
        };

         /** @type {AnalyticsFacade['getDataForCallChart']} */
        const _getDataForCallChart = function(
            startMoment,
            endMoment,
            callType,
            filterOnHostedNumbers,
            filterOnSystemObjects,
            filterOnLiveReceptionistScripts) {
            const { hostedNumbers, userGroups, receptionistScripts } = _unwrap(filterOnHostedNumbers, filterOnSystemObjects, filterOnLiveReceptionistScripts);

            return _promiseFactory.deferIndefinitely(function(promise) {
                /** @type {IAnalyticsGetRequest} */
                const requestData = {
                    callType: callType,
                    endDate: endMoment.format(),
                    hostedNumbers: hostedNumbers,
                    receptionistScripts,
                    startDate: startMoment.format(),
                    usersAndUserGroups: userGroups,
                };

                _analyticsSocket.send(`getInitial`, requestData, (results, err) => {
                    if (err) {
                        promise.reject(new Error(err));
                        return;
                    }

                    promise.resolve(results);
                });
            });
        };

        /** @type {AnalyticsFacade['getDataForChatChart']} */
        const _getDataForChatChart = function(
            startMoment,
            endMoment) {
            return _promiseFactory.deferIndefinitely(function(promise) {
                const requestData = {
                    endDate: endMoment.format(),
                    startDate: startMoment.format(),
                };

                _analyticsSocket.send(`getInitialChats`, requestData, (results, err) => {
                    if (err) {
                        promise.reject(new Error(err));
                        return;
                    }

                    promise.resolve(results);
                });
            });
        };

        /** @type {AnalyticsFacade['getCallRecords']} */
        const _getCallRecords = function(
            startMoment,
            endMoment,
            callType,
            filterOnHostedNumbers,
            filterOnSystemObjects,
            filterOnLiveReceptionistScripts) {
            const { hostedNumbers, userGroups, receptionistScripts } = _unwrap(filterOnHostedNumbers, filterOnSystemObjects, filterOnLiveReceptionistScripts);

            return _promiseFactory.deferIndefinitely(function(promise) {
                /** @type {IAnalyticsGetRequest} */
                const requestData = {
                    callType: callType,
                    endDate: endMoment.format(),
                    hostedNumbers: hostedNumbers,
                    receptionistScripts,
                    startDate: startMoment.format(),
                    usersAndUserGroups: userGroups,
                };

                _analyticsSocket.send("getCallRecords", requestData, (results, err) => {
                    if (err) {
                        promise.reject(new Error(err));
                        return;
                    }

                    promise.resolve(results);
                });
            });
        };

        /** @type {AnalyticsFacade['getChatRecords']} */
        const _getChatRecords = function(startMoment, endMoment) {
            return _promiseFactory.deferIndefinitely(function(promise) {
                /** @type {IAnalyticsGetRequest} */
                const requestData = {
                    endDate: endMoment.format(),
                    startDate: startMoment.format()
                };

                _analyticsSocket.send("getChatRecords", requestData, (results, err) => {
                    if (err) {
                        promise.reject(new Error(err));
                        return;
                    }

                    promise.resolve(results);
                });
            });
        };

        /** @type {AnalyticsFacade['getTimeZoneForUser']} */
        const _getTimeZoneForUser = function() {
            return _promiseFactory.defer(function(promise) {
                _analyticsSocket.send("getTimeZone", {}, (timeZone, err) => {
                    if (err) {
                        promise.reject(new Error(err));
                        return;
                    }

                    promise.resolve(timeZone);
                });
            });
        };

        return function () {
            const self = this;

            self.init = _init;
            self.getCallRecords = _getCallRecords;
            self.getChatRecords = _getChatRecords;
            self.getDataForCallChart = _getDataForCallChart;
            self.getDataForChatChart = _getDataForChatChart;
            self.getTimeZoneForUser = _getTimeZoneForUser;
            self.getCallsCsv = _getCallsCsv;
            self.getStatsCsv = _getStatsCsv;
        };
    }
);

